import { apiClient } from './apiClient';
import type {
  ControlPoint,
  ControlPointDetail,
  ControlPointStatistics,
  ControlPointFromAuditFormValues,
  ControlPointEditFormValues,
  ControlPointReviewFormValues,
  ControlPointFilters,
  VarietySample,
} from '../../types/control-point';

/**
 * Error response structure from API
 */
interface ApiError {
  message: string;
  status?: number;
  originalError?: any;
}

/**
 * Paginated response structure
 */
interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Control Point API Service
 * Handles all control point-related API operations
 */
export class ControlPointService {
  /**
   * Get all control points with optional filters
   * 
   * @param filters - Optional filters to apply
   * @returns Promise with array of control points
   * 
   * @example
   * ```typescript
   * const controlPoints = await controlPointService.getControlPoints({
   *   status: 'open',
   *   farm: 123
   * });
   * ```
   */
  async getControlPoints(filters?: ControlPointFilters): Promise<ControlPoint[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });
      }

      const queryString = params.toString();
      const url = queryString ? `/control-points/?${queryString}` : '/control-points/';
      
      const response = await apiClient.get<any>(url);
      
      // Handle GeoJSON FeatureCollection format
      if (response.data && response.data.type === 'FeatureCollection') {
        // Extract properties from GeoJSON features
        return response.data.features.map((feature: any) => ({
          ...feature.properties,
          id: feature.id || feature.properties.id,
          geom: feature.geometry,
        }));
      }
      
      // Handle paginated response
      if (response.data && response.data.results) {
        return response.data.results;
      }
      
      // Handle direct array
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return [];
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.fetch_failed');
    }
  }

  /**
   * Get a single control point by ID with full details
   * 
   * @param id - The ID of the control point
   * @returns Promise with the control point detail
   * 
   * @example
   * ```typescript
   * const controlPoint = await controlPointService.getControlPoint('uuid-123');
   * ```
   */
  async getControlPoint(id: string): Promise<ControlPointDetail> {
    try {
      const response = await apiClient.get<ControlPointDetail>(
        `/control-points/${id}/`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.fetch_failed');
    }
  }



  /**
   * Update an existing control point
   * 
   * @param id - The ID of the control point to update
   * @param data - The partial control point data to update
   * @returns Promise with the updated control point
   * 
   * @example
   * ```typescript
   * const updatedControlPoint = await controlPointService.updateControlPoint('uuid-123', {
   *   status: 'closed',
   *   closed_date: '2024-01-15',
   *   comment_closed: 'Converted to authorized farm'
   * });
   * ```
   */
  async updateControlPoint(
    id: string,
    data: ControlPointEditFormValues
  ): Promise<ControlPoint> {
    try {
      const response = await apiClient.patch<ControlPoint>(
        `/control-points/${id}/`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.update_failed');
    }
  }

  /**
   * Delete a control point
   * 
   * @param id - The ID of the control point to delete
   * @returns Promise that resolves when deletion is complete
   * 
   * @example
   * ```typescript
   * await controlPointService.deleteControlPoint('uuid-123');
   * ```
   */
  async deleteControlPoint(id: string): Promise<void> {
    try {
      await apiClient.delete(`/control-points/${id}/`);
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.delete_failed');
    }
  }

  /**
   * Get statistics for control points dashboard
   * 
   * @returns Promise with control point statistics
   * 
   * @example
   * ```typescript
   * const stats = await controlPointService.getStatistics();
   * console.log(`Total control points: ${stats.total_control_points}`);
   * ```
   */
  async getStatistics(): Promise<ControlPointStatistics> {
    try {
      const response = await apiClient.get<ControlPointStatistics>(
        '/control-points/statistics/'
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.statistics_failed');
    }
  }

  /**
   * Get comprehensive dashboard data with optional country filter
   * 
   * @param countryId - Optional country ID to filter data
   * @returns Promise with dashboard data
   * 
   * @example
   * ```typescript
   * const dashboardData = await controlPointService.getDashboardData(1);
   * ```
   */
  async getDashboardData(countryId?: number): Promise<any> {
    try {
      const url = countryId 
        ? `/control-points/dashboard-data/?country_id=${countryId}`
        : '/control-points/dashboard-data/';
      
      const response = await apiClient.get(url);
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.dashboard_data_failed');
    }
  }

  /**
   * Get all samples for a specific control point
   * 
   * @param id - The ID of the control point
   * @returns Promise with array of variety samples
   * 
   * @example
   * ```typescript
   * const samples = await controlPointService.getSamples('uuid-123');
   * ```
   */
  async getSamples(id: string): Promise<VarietySample[]> {
    try {
      const response = await apiClient.get<VarietySample[]>(
        `/control-points/${id}/samples/`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.samples_failed');
    }
  }

  /**
   * Create a control point from an audit context
   * 
   * This endpoint automatically associates the control point with the audit's farm
   * and creates the first review linked to the audit.
   * 
   * @param auditId - The ID of the audit
   * @param data - The control point data (without farm field)
   * @returns Promise with the created control point
   * 
   * @example
   * ```typescript
   * const newControlPoint = await controlPointService.createFromAudit(123, {
   *   reason: 1,
   *   is_producer_owner: true,
   *   comment: 'Detected during audit',
   *   estimated_surface: 0.5,
   *   geom: { type: 'Point', coordinates: [-70.6693, -33.4489] },
   *   samples: [{ code_sample: 'LAB001', position_waypoint: 'WP001' }]
   * });
   * ```
   */
  async createFromAudit(
    auditId: number,
    data: ControlPointFromAuditFormValues
  ): Promise<ControlPointDetail> {
    try {
      const payload = {
        ...data,
        audit_id: auditId
      };
      
      const response = await apiClient.post<ControlPointDetail>(
        '/audit-control-points/create-from-audit/',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.create_from_audit_failed');
    }
  }

  /**
   * Get all OPEN control points for a specific farm
   * Used when starting an audit to show which control points need to be reviewed
   * 
   * @param farmId - The ID of the farm
   * @returns Promise with array of open control points
   * 
   * @example
   * ```typescript
   * const openControlPoints = await controlPointService.getOpenControlPointsForFarm(123);
   * ```
   */
  async getOpenControlPointsForFarm(
    farmId: number, 
    auditId?: number
  ): Promise<{
    control_points: ControlPoint[];
    editable_control_point_ids?: string[];
    audit_id?: number;
    audit_date?: string;
  }> {
    try {
      const url = auditId 
        ? `/audit-control-points/open-for-farm/?farm_id=${farmId}&audit_id=${auditId}`
        : `/audit-control-points/open-for-farm/?farm_id=${farmId}`;
      
      const response = await apiClient.get<{
        control_points: ControlPoint[];
        editable_control_point_ids?: string[];
        audit_id?: number;
        audit_date?: string;
      }>(url);
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.fetch_failed');
    }
  }

  /**
   * Create a new review for an existing control point during an audit
   * 
   * @param data - The review data to create
   * @returns Promise with the created review
   * 
   * @example
   * ```typescript
   * const newReview = await controlPointService.createReview({
   *   audit_id: 456,
   *   control_point_id: 'uuid-123',
   *   comment: 'Follow-up inspection',
   *   estimated_surface: 0.6,
   *   samples: [{ code_sample: 'LAB002', position_waypoint: 'WP002' }]
   * });
   * ```
   */
  async createReview(data: ControlPointReviewFormValues): Promise<any> {
    try {
      const response = await apiClient.post(
        '/audit-control-points/create-review/',
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.review_create_failed');
    }
  }

  /**
   * Close a control point
   * 
   * @param id - The ID of the control point to close
   * @param data - The closure data
   * @returns Promise with the updated control point
   * 
   * @example
   * ```typescript
   * const closedCP = await controlPointService.closeControlPoint('uuid-123', {
   *   closed_date: '2024-01-15',
   *   comment_closed: 'Converted to authorized farm',
   *   converted_hectares: 2.5,
   *   authorized_farm_reference: 'Finca XYZ'
   * });
   * ```
   */
  async closeControlPoint(
    id: string,
    data: {
      closed_date: string;
      comment_closed: string;
      converted_hectares?: number;
      authorized_farm_reference?: string;
    }
  ): Promise<ControlPointDetail> {
    try {
      const response = await apiClient.post<ControlPointDetail>(
        `/control-points/${id}/close/`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.close_failed');
    }
  }

  /**
   * Update a control point review
   * 
   * @param reviewId - The ID of the review to update
   * @param data - The review data to update
   * @returns Promise with the updated review
   */
  async updateReview(reviewId: string, data: { comment: string; estimated_surface: number }): Promise<any> {
    try {
      const response = await apiClient.patch(`/control-point-reviews/${reviewId}/`, data);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.update_review_failed');
    }
  }

  /**
   * Update samples for a review
   * 
   * @param reviewId - The ID of the review
   * @param samples - Array of samples to update
   * @returns Promise with the updated review
   */
  async updateReviewSamples(reviewId: string, samples: SampleInput[]): Promise<any> {
    try {
      const response = await apiClient.patch(`/control-point-reviews/${reviewId}/update_samples/`, {
        samples
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.control_point.update_samples_failed');
    }
  }

  /**
   * Handle API errors and provide translated error messages
   * 
   * @param error - The error object from the API
   * @param defaultMessageKey - Default translation key for the error message
   * @returns Formatted error object
   */
  private handleError(error: any, defaultMessageKey: string): ApiError {
    // If error already has a message property (from apiClient interceptor)
    if (error.message) {
      return {
        message: error.message,
        status: error.status,
        originalError: error.originalError,
      };
    }

    // Handle axios error response
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Extract error message from various possible formats
      let message = defaultMessageKey;
      
      if (typeof errorData === 'string') {
        message = errorData;
      } else if (errorData.error) {
        message = errorData.error;
      } else if (errorData.detail) {
        message = errorData.detail;
      } else if (errorData.message) {
        message = errorData.message;
      } else if (errorData.errors) {
        // Handle validation errors
        if (Array.isArray(errorData.errors)) {
          message = errorData.errors.join(', ');
        } else if (typeof errorData.errors === 'object') {
          message = Object.values(errorData.errors).flat().join(', ');
        }
      }

      return {
        message,
        status: error.response.status,
        originalError: error,
      };
    }

    // Handle network or other errors
    return {
      message: error.message || defaultMessageKey,
      originalError: error,
    };
  }
}

/**
 * Singleton instance of ControlPointService
 */
export const controlPointService = new ControlPointService();

export default controlPointService;
